import React, { useEffect } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip } from '@material-ui/core';
import axios from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import * as yup from 'yup';

import FormAutocomplete from 'components/FormAutocomplete';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import members from 'config/api/members/members';
/** Used when the “Back to owner” shortcut is enabled (currently commented out in this file). */
// import useIsOwner from 'hooks/useIsOwner/useIsOwner';
import action_library_messages from 'messages/action_library_messages';
import coworkerOptionsParser from 'services/autocompleteServices/coworkerOptionsParser/coworkerOptionsParser';
import coworkerParamsGetter from 'services/autocompleteServices/coworkerParamsGetter/coworkerParamsGetter';
import coworkerResultComponent from 'services/autocompleteServices/coworkerResultComponent/coworkerResultComponent';

type Props = {
  open: boolean;
  onClose: () => void;
  instance: ActionLibraryInstance;
  onSuccess: () => void;
};

const fetchCoworkersForDelegate = async () => {
  const config = members.search({ limit: 100, search: '' });
  const { data } = await axios(config);
  return coworkerOptionsParser(data);
};

/** Query keys to invalidate after successful delegation (AC 5.1 – list/dashboard refresh; AC 3.2 – status log refresh). */
const DELEGATION_SUCCESS_QUERY_KEYS = ['ACTION_LIBRARY_INSTANCES_GET_ALL'] as const;

type DelegateOption = { key: number; fullName: string };

const DelegateInstanceDialog: React.FC<Props> = ({ open, onClose, instance, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  /**
   * “Back to owner” (`showBackToOwnerButton`) is disabled until the backend supports it.
   * When re-enabling, uncomment together: `useIsOwner` + `isCurrentUserOwner`, `showBackToOwnerButton`,
   * `handleBackToOwner`, the button JSX next to `FormAutocomplete`, and add `showBackToOwnerButton &&`
   * to the `coworkerOptions` prepend condition (see comment there).
   */
  // const { checkIsOwner } = useIsOwner();
  // const isCurrentUserOwner = checkIsOwner(instance.owner);

  const { data: rawCoworkerOptions = [], isLoading: coworkersLoading } = useQuery(
    ['COWORKERS_DELEGATE_PRELOAD'],
    fetchCoworkersForDelegate,
    { enabled: open },
  );
  const baseCoworkerOptions = rawCoworkerOptions.filter(
    (o: { key?: number; fullName?: string }): o is DelegateOption => !!o?.key && !!o?.fullName,
  );

  const ownerOption: DelegateOption | null =
    instance.owner != null && instance.ownerFullName ? { key: instance.owner, fullName: instance.ownerFullName } : null;

  // TODO: Uncomment when backend is updated (see checklist above).
  // const showBackToOwnerButton = instance.delegatedTo != null && ownerOption != null;

  // Temporary: prepend owner if absent from search results. When “Back to owner” is restored, require
  // `showBackToOwnerButton &&` here so the owner appears in the list only for delegated instances.
  const coworkerOptions: DelegateOption[] =
    ownerOption && !baseCoworkerOptions.some((o: DelegateOption) => o.key === ownerOption.key)
      ? [ownerOption, ...baseCoworkerOptions]
      : baseCoworkerOptions;

  const delegateMutation = useMutation(
    (payload: { delegatedTo: number | null; delegationComment: string }) =>
      actionLibraryInstancesApi.delegateActionLibraryInstance(instance.id, payload),
    {
      onSuccess: () => {
        DELEGATION_SUCCESS_QUERY_KEYS.forEach(key => queryClient.invalidateQueries(key, { refetchInactive: true }));
        queryClient.invalidateQueries(['ACTION_LIBRARY_INSTANCE_STATUS_LOGS', instance.id], {
          refetchInactive: true,
        });
        enqueueSnackbar(t(action_library_messages.success.instance_updated), { variant: 'success' });
        onSuccess();
        onClose();
      },
      onError: (err: { response?: { status?: number } }) => {
        const status = err?.response?.status;
        let message = t(action_library_messages.errors.delegate_failed);
        if (status === 409) {
          message = t(action_library_messages.instance_actions.delegate_concurrency_error);
        } else if (status === 403) {
          message = t(action_library_messages.errors.delegate_forbidden);
        }
        enqueueSnackbar(message, { variant: 'error' });
      },
    },
  );

  const validationSchema = React.useMemo(
    () =>
      yup.object({
        delegatedTo: yup
          .array()
          .min(1, t(action_library_messages.instance_actions.delegate_dialog_user_required))
          .required(t(action_library_messages.instance_actions.delegate_dialog_user_required)),
        delegationComment: yup.string().trim().required(t(action_library_messages.instance_actions.delegate_dialog_reason_required)),
      }),
    [t],
  );

  const formik = useFormik<{ delegatedTo: DelegateOption[]; delegationComment: string }>({
    initialValues: {
      delegatedTo: [],
      delegationComment: '',
    },
    validationSchema,
    onSubmit: async values => {
      const userId = values.delegatedTo?.[0]?.key ?? null;

      try {
        // Step 1: Fetch the latest instance data to check for concurrency
        const { data: latestInstance } = await actionLibraryInstancesApi.getSingleActionLibraryInstance(instance.id);

        if (latestInstance.updatedAt !== instance.updatedAt) {
          // Concurrency conflict detected on the frontend
          enqueueSnackbar(t(action_library_messages.instance_actions.delegate_concurrency_error), { variant: 'error' });
          onClose(); // Force close to ensure user reloads
          return;
        }

        // If no conflict, proceed with the delegation
        delegateMutation.mutate({
          delegatedTo: userId,
          delegationComment: values.delegationComment.trim(),
        });
      } catch (error) {
        enqueueSnackbar(t(action_library_messages.errors.something_went_wrong_try_later), { variant: 'error' });
      }
    },
  });

  // TODO: Uncomment with `showBackToOwnerButton` (see checklist near top of component).
  // const handleBackToOwner = () => {
  //   if (ownerOption) formik.setFieldValue('delegatedTo', [ownerOption]);
  // };

  useEffect(() => {
    if (open) {
      formik.setFieldValue('delegatedTo', []);
      formik.setFieldValue('delegationComment', '');
    }
  }, [open, instance.id, instance.delegatedTo, instance.owner, instance.ownerFullName]);

  const hasUser = (formik.values.delegatedTo?.length ?? 0) > 0;
  const hasReason = formik.values.delegationComment.trim().length > 0;
  const isSaveDisabled = delegateMutation.isLoading || !hasUser || !hasReason;
  const saveDisabledTooltip = t(action_library_messages.instance_actions.delegate_save_disabled_tooltip);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{t(action_library_messages.instance_actions.delegate_dialog_title)}</DialogTitle>
        <DialogContent>
          <Box alignItems='flex-start' display='flex' width='100%'>
            <Box flex={1} minWidth={0} style={{ marginRight: 8 }}>
              <FormAutocomplete
                apiCallParamsGetter={coworkerParamsGetter}
                customizeLabel={coworkerResultComponent}
                formik={formik}
                id='delegatedTo'
                initialOptions={coworkerOptions}
                initialOptionsLoading={coworkersLoading}
                label={`${t(action_library_messages.instance_actions.delegate_dialog_select_user)} *`}
                multiple={false}
                optionsParser={coworkerOptionsParser}
              />
            </Box>
            {/* “Back to owner” button — TODO: uncomment with `showBackToOwnerButton` (checklist near top). */}
            {/* {showBackToOwnerButton && (
              <Tooltip title={isCurrentUserOwner ? t(action_library_messages.instance_actions.delegate_back_to_owner_disabled_owner) : ''}>
                <span style={{ display: 'inline-block' }}>
                  <Button
                    color='secondary'
                    disabled={isCurrentUserOwner}
                    onClick={handleBackToOwner}
                    size='large'
                    style={{ minHeight: 56 }}
                    variant='outlined'
                  >
                    {t(action_library_messages.instance_actions.delegate_back_to_owner)}
                  </Button>
                </span>
              </Tooltip>
            )} */}
          </Box>
          <TextField
            error={formik.touched.delegationComment && Boolean(formik.errors.delegationComment)}
            fullWidth
            helperText={formik.touched.delegationComment && formik.errors.delegationComment}
            label={t(action_library_messages.instance_actions.delegate_dialog_reason)}
            margin='normal'
            multiline
            name='delegationComment'
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            required
            rows={3}
            value={formik.values.delegationComment}
            variant='outlined'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t(action_library_messages.steps.cancel)}</Button>
          <Tooltip title={isSaveDisabled ? saveDisabledTooltip : ''}>
            <span>
              <Button color='primary' disabled={isSaveDisabled} type='submit' variant='contained'>
                {t(action_library_messages.instance_actions.delegate)}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DelegateInstanceDialog;
